import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import produce from 'immer';
import { catchError, Observable, tap } from 'rxjs';
import { buildCreatePaymentMethodRequest, mapPaymentMethod, mapPaymentMethods, PaymentMethod } from '../../models';
import { ApiService, SentryService } from '../../services';
import {
  FetchPatientPaymentMethods,
  SetDefaultPatientPaymentMethod,
  CreatePatientPaymentMethod,
} from './payment-methods.actions';
import { ApiStoreData, defaultApiStoreIterableData, defaultApiStoreData, MissingApiRequestDataError } from '../utils';
import { PatientState } from '../patient/patient.state';

export interface PaymentMethodsStateModel {
  paymentMethods: ApiStoreData<PaymentMethod[]>;
  createPaymentMethod: ApiStoreData<PaymentMethod | null>;
  setDefaultPaymentMethod: ApiStoreData<null>;
}

@State<PaymentMethodsStateModel>({
  name: 'patientPaymentMethods',
  defaults: {
    paymentMethods: defaultApiStoreIterableData,
    createPaymentMethod: defaultApiStoreData,
    setDefaultPaymentMethod: defaultApiStoreData,
  },
})
@Injectable({ providedIn: 'root' })
export class PaymentMethodsState {
  constructor(private apiService: ApiService, private store: Store, private sentry: SentryService) {}

  handleError = this.sentry.handleError;
  handleSuccess = this.sentry.handleSuccess;

  @Selector()
  static paymentMethods(state: PaymentMethodsStateModel): PaymentMethod[] {
    return state.paymentMethods.data;
  }

  @Selector()
  static paymentMethodsState(state: PaymentMethodsStateModel): ApiStoreData<PaymentMethod[]> {
    return state.paymentMethods;
  }

  @Selector()
  static defaultPaymentMethod(state: PaymentMethodsStateModel): PaymentMethod | null {
    return state.paymentMethods.data.find((pm) => pm.isDefault) || null;
  }

  @Selector()
  static createPaymentMethodState(state: PaymentMethodsStateModel): ApiStoreData<PaymentMethod | null> {
    return state.createPaymentMethod;
  }

  @Selector()
  static setDefaultPaymentMethodState(state: PaymentMethodsStateModel): ApiStoreData<null> {
    return state.setDefaultPaymentMethod;
  }

  @Action(FetchPatientPaymentMethods)
  fetchPatientPaymentMethods(ctx: StateContext<PaymentMethodsStateModel>) {
    ctx.setState(
      produce((draft) => {
        draft.paymentMethods.isLoading = true;
      })
    );

    const patientId = this.store.selectSnapshot(PatientState.selectedPatientId);

    if (!patientId) {
      const error = new MissingApiRequestDataError(['Missing patient id']);
      return this.handleError({ ctx, error, stateKey: 'paymentMethods' });
    }

    return this.apiService.getPaymentMethods(patientId).pipe(
      tap({
        next: (response) => {
          const data = mapPaymentMethods(response.data);
          this.handleSuccess({ ctx, data, stateKey: 'paymentMethods' });
        },
      }),
      catchError((error: Error): Observable<any> => this.handleError({ ctx, error, stateKey: 'paymentMethods' }))
    );
  }

  @Action(CreatePatientPaymentMethod)
  createPatientPaymentMethod(ctx: StateContext<PaymentMethodsStateModel>, action: CreatePatientPaymentMethod) {
    ctx.setState(
      produce((draft) => {
        draft.createPaymentMethod.isLoading = true;
      })
    );

    const patientId = this.store.selectSnapshot(PatientState.selectedPatientId);
    const paymentMethods = ctx.getState().paymentMethods.data;
    const stateKey = 'createPaymentMethod';

    if (!patientId) {
      const error = new MissingApiRequestDataError(['Missing patient id']);
      return this.handleError({ ctx, error, stateKey });
    }

    return this.apiService
      .createPaymentMethod(patientId, buildCreatePaymentMethodRequest(action.formValues, paymentMethods))
      .pipe(
        tap({
          next: (response) => {
            const newPaymentMethod = mapPaymentMethod(response.data);

            const updateStateFunction = (draft: PaymentMethodsStateModel) => {
              if (action.formValues.isDefault) {
                draft.paymentMethods.data.forEach((pm) => (pm.isDefault = false));
              }

              draft.paymentMethods.data = [...draft.paymentMethods.data, newPaymentMethod];
            };

            this.handleSuccess({
              ctx: ctx,
              data: newPaymentMethod,
              stateKey,
              updateStateFunction,
            });
          },
        }),
        catchError((error: Error): Observable<any> => this.handleError({ ctx, error, stateKey }))
      );
  }

  @Action(SetDefaultPatientPaymentMethod)
  setDefaultPatientPaymentMethod(ctx: StateContext<PaymentMethodsStateModel>, action: SetDefaultPatientPaymentMethod) {
    // Optimistic update
    const previousDefaultPaymentMethod = PaymentMethodsState.defaultPaymentMethod(ctx.getState());

    ctx.setState(
      produce((draft) => {
        draft.setDefaultPaymentMethod.isLoading = true;
        draft.paymentMethods.data.forEach((pm) => (pm.isDefault = false));
        draft.paymentMethods.data.find((pm) => pm.id === action.paymentMethodId)!.isDefault = true;
      })
    );

    const stateKey = 'setDefaultPaymentMethod';
    const patientId = this.store.selectSnapshot(PatientState.selectedPatientId);

    if (!patientId) {
      const error = new MissingApiRequestDataError(['Missing patient id']);
      return this.handleError({ ctx, error, stateKey });
    }

    return this.apiService.editPaymentMethod(patientId, action.paymentMethodId, { default: true }).pipe(
      tap({
        next: () => {
          this.handleSuccess({ ctx, stateKey });
        },
      }),
      catchError((error: Error): Observable<any> => {
        const updateStateFunction = (draft: PaymentMethodsStateModel) => {
          draft.paymentMethods.data.forEach((pm) => (pm.isDefault = false));
          draft.paymentMethods.data.find((pm) => pm.id === previousDefaultPaymentMethod!.id)!.isDefault = true;
        };

        return this.handleError({ ctx, error, stateKey, updateStateFunction });
      })
    );
  }
}
